import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMerchantAmenities,
  fetchMerchantAttributes,
} from "../../redux/actions";
import Swiper from "swiper";
import { Autoplay } from "swiper/modules";
import * as Styled from "./mobile.style";
import {
  ArrowBack,
  CheckCircle,
  CheckCircleFilled,
  Event,
  EventBusy,
  LocalOffer,
  Place,
  Star,
  Storefront,
} from "./mobile.icons.jsx";
import "swiper/css";
import { assetUrl } from "../../helpers";

/**
 * How this voucher will look in the Felix app.
 *
 * This is a 1:1 reproduction of
 * `felix_app/lib/pages/home_page/vouchers_page/voucher_page.dart`: the same
 * sections, in the same order, under the same conditions, with the same
 * copy. Anything that screen shows is shown here; anything it does not show is
 * not invented. Previously this preview diverged from the app in both
 * directions -- it omitted the header, the merchant mark, the date range, the
 * opening hours, the terms and the included plans, and it added an "N
 * available" chip that the app never renders.
 *
 * Keep the two in step: if the Dart screen gains a section, add it here.
 */

/** voucher_page.dart `_formatHour` — values <= 24 are hours, else minutes. */
const formatHour = (value) => {
  if (value === null || value === undefined) return "--";
  const minutesSinceMidnight = value <= 24 ? value * 60 : value;
  const hour = Math.floor(minutesSinceMidnight / 60) % 24;
  const minute = minutesSinceMidnight % 60;
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${period}`;
};

/**
 * voucher.model.dart `Terms.licenceLines` — the licence JSON rendered as
 * readable lines, e.g. {"transferable": false} -> "Transferable: No".
 */
const licenceLinesOf = (terms) =>
  Object.entries(terms?.license ?? {}).map(([key, value]) => {
    const label = key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
    const rendered =
      typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
    return `${label}: ${rendered}`;
  });

/** DateFormat('MMM d') / DateFormat('MMM d, yyyy') */
const formatChipDate = (value, withYear) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
  });
};

const InfoChip = ({ icon: Icon, label, iconUrl }) => (
  <Styled.Chip>
    {/* _InfoChip falls through to the Material icon when the uploaded icon is
        missing or empty, rather than requesting a bare base URL. */}
    {iconUrl ? (
      <img src={assetUrl(iconUrl)} alt="" onError={(e) => e.target.remove()} />
    ) : (
      <Icon />
    )}
    {label}
  </Styled.Chip>
);

export const Mobile = ({
  voucherName,
  voucherDesc,
  voucherCategory,
  voucherImages,
  voucherScope,
  startDate,
  endDate,
  excludeHolidays,
  subscriptionPlans,
  openingHours,
  termsAndConditions,
  exhausted = false,
  redeemedCount = 0,
  voucherCount,
  merchant: merchantProp,
}) => {
  const dispatch = useDispatch();
  // The create wizard collects the merchant into redux as you pick it; the edit
  // screen has no Merchant Details tab, so redux is empty there and the
  // merchant comes in on the voucher payload instead. The app always shows the
  // merchant, so the preview must have one from whichever source exists.
  const storeMerchant = useSelector(({ merchant }) => merchant?.merchant);
  const merchant = merchantProp ?? storeMerchant;
  const carouselRef = useRef(null);
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    dispatch(fetchMerchantAmenities());
    dispatch(fetchMerchantAttributes());
  }, [dispatch]);

  const images = useMemo(
    () => (voucherImages || []).filter(Boolean),
    [voucherImages]
  );
  const imageCount = images.length;

  // Swiper is built against the ref rather than a selector: the carousel only
  // exists once there is an image, so a selector bound it to nothing and the
  // later update() threw "swiper.el.querySelectorAll is not a function".
  useEffect(() => {
    if (!carouselRef.current || imageCount === 0) {
      swiperRef.current?.destroy?.(true, true);
      swiperRef.current = null;
      return undefined;
    }

    swiperRef.current = new Swiper(carouselRef.current, {
      slidesPerView: 1,
      // voucher_page.dart: Swiper(loop: false)
      loop: false,
      autoplay:
        imageCount > 1 ? { delay: 2600, pauseOnMouseEnter: true } : false,
      modules: [Autoplay],
      on: {
        slideChange: (instance) => setActiveIndex(instance.activeIndex),
      },
    });

    return () => {
      swiperRef.current?.destroy?.(true, true);
      swiperRef.current = null;
    };
  }, [imageCount]);

  useEffect(() => {
    if (swiperRef.current?.el) swiperRef.current.update();
  }, [voucherImages, merchant]);

  const location = [merchant?.address, merchant?.city]
    .filter((part) => part && String(part).trim())
    .join(", ");

  const startLabel = startDate ? formatChipDate(startDate, false) : null;
  const endLabel = endDate ? formatChipDate(endDate, true) : null;

  const amenities = merchant?.amenities ?? [];
  const attributes = merchant?.attributes ?? [];
  const plans = (subscriptionPlans ?? []).filter((p) => p?.plan_name || p?.label);
  const hours = Object.entries(openingHours ?? {});
  const licenceLines = licenceLinesOf(termsAndConditions);

  return (
    <Styled.Phone>
      {/* Scaffold header: back button, centred "Voucher", 44px spacer. */}
      <Styled.Header>
        <Styled.BackButton>
          <ArrowBack />
        </Styled.BackButton>
        <Styled.HeaderTitle>Voucher</Styled.HeaderTitle>
        <Styled.HeaderSpacer />
      </Styled.Header>

      <Styled.Scroll>
        <Styled.Media>
          {imageCount > 0 && (
            <div className="swiper" ref={carouselRef}>
              <div className="swiper-wrapper">
                {images.map((image, index) => (
                  <div className="swiper-slide" key={index}>
                    <img
                      src={assetUrl(image.dataURL || image)}
                      alt={`${voucherName || "Voucher"} ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* The badge is the *merchant* category, not the voucher category. */}
          {merchant?.merchant_category?.category_name && (
            <Styled.MediaBadge>
              {merchant.merchant_category.category_name}
            </Styled.MediaBadge>
          )}
          {exhausted && <Styled.SoldOutBadge>Sold out</Styled.SoldOutBadge>}
        </Styled.Media>

        {imageCount > 1 && (
          <Styled.Dots>
            {images.map((_, index) => (
              <span
                key={index}
                className={index === activeIndex ? "is-active" : ""}
              />
            ))}
          </Styled.Dots>
        )}

        <Styled.TitleRow>
          <Styled.TitleBlock>
            <Styled.VoucherName>{voucherName || ""}</Styled.VoucherName>
            <Styled.MerchantName>
              {merchant?.merchant_name || ""}
            </Styled.MerchantName>
            {location && (
              <Styled.Location>
                <Place />
                <span>{location}</span>
              </Styled.Location>
            )}
          </Styled.TitleBlock>
          {merchant?.image && (
            <Styled.MerchantMark
              src={assetUrl(merchant.image)}
              alt={merchant?.merchant_name || ""}
              onError={(e) => e.target.remove()}
            />
          )}
        </Styled.TitleRow>

        <Styled.ChipRow>
          {voucherCategory?.category_name && (
            <InfoChip
              icon={LocalOffer}
              label={voucherCategory.category_name}
              iconUrl={voucherCategory?.voucher_category_icon}
            />
          )}
          {voucherScope && <InfoChip icon={Storefront} label={voucherScope} />}
          {startLabel && endLabel && (
            <InfoChip icon={Event} label={`${startLabel} - ${endLabel}`} />
          )}
          {excludeHolidays && (
            <InfoChip icon={EventBusy} label="Excludes holidays" />
          )}
        </Styled.ChipRow>

        {redeemedCount > 0 && (
          <Styled.RedeemedBanner>
            <CheckCircleFilled />
            <span>
              {voucherCount
                ? `Redeemed ${redeemedCount} of ${voucherCount} times`
                : `Redeemed ${redeemedCount} times`}
            </span>
          </Styled.RedeemedBanner>
        )}

        {voucherDesc && (
          <Styled.Section>
            <Styled.SectionTitle>Description</Styled.SectionTitle>
            <Styled.Body>{voucherDesc}</Styled.Body>
          </Styled.Section>
        )}

        {(amenities.length > 0 || attributes.length > 0) && (
          <Styled.Section>
            <Styled.SectionTitle>At this location</Styled.SectionTitle>
            <Styled.TagRow>
              {amenities.map(({ id, amenity_name }) => (
                <InfoChip key={`a-${id}`} icon={CheckCircle} label={amenity_name} />
              ))}
              {attributes.map(({ id, attribute_name }) => (
                <InfoChip key={`t-${id}`} icon={Star} label={attribute_name} />
              ))}
            </Styled.TagRow>
          </Styled.Section>
        )}

        {termsAndConditions?.description && (
          <Styled.Section>
            <Styled.SectionTitle>Terms &amp; Conditions</Styled.SectionTitle>
            <Styled.Body>{termsAndConditions.description}</Styled.Body>
            {licenceLines.length > 0 && (
              <Styled.LicenceCard>
                {licenceLines.map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </Styled.LicenceCard>
            )}
          </Styled.Section>
        )}

        {hours.length > 0 && (
          <Styled.Section>
            <Styled.SectionTitle>Opening Hours</Styled.SectionTitle>
            <Styled.HoursCard>
              {hours.map(([day, value]) => (
                <Styled.HoursRow key={day}>
                  <span className="day">{day}</span>
                  <span className="hours">
                    {value
                      ? `${formatHour(value.min ?? value.open)} - ${formatHour(
                          value.max ?? value.close
                        )}`
                      : "Closed"}
                  </span>
                </Styled.HoursRow>
              ))}
            </Styled.HoursCard>
          </Styled.Section>
        )}

        {plans.length > 0 && (
          <Styled.Section>
            <Styled.SectionTitle>Included Plans</Styled.SectionTitle>
            <Styled.TagRow>
              {plans.map((plan, index) => (
                <Styled.PlanPill key={plan.id ?? index}>
                  {plan.plan_name || plan.label}
                </Styled.PlanPill>
              ))}
            </Styled.TagRow>
          </Styled.Section>
        )}
      </Styled.Scroll>

      <Styled.Cta $exhausted={exhausted}>
        {exhausted ? "No longer available" : "Redeem"}
      </Styled.Cta>
    </Styled.Phone>
  );
};

export default Mobile;
